import React from 'react';

// The app had NO error boundary, which is why one throwing component took the
// WHOLE screen with it: React unmounts the entire tree on an uncaught render
// error, and on a dark theme an empty tree is a black rectangle. Tom, on
// Profile, 2026-07-17: "a completely black screen, i can see nothing" — nav and
// all, because the nav lived inside the tree that died.
//
// This is the third blank-screen of this family (the League-only white screen
// 2026-07-17, the admin reply-brief blank 2026-07-06). The bugs behind them
// differed; the reason each was INVISIBLE rather than merely broken was always
// the same missing boundary. So the boundary is the fix for the class: one bad
// card degrades to a legible message you can retry, and everything outside it
// keeps working.
//
// It also PRINTS the error. A black screen tells you nothing and costs a
// debugging session; the message names the file and the line.
// The signatures browsers use for a lazy chunk that 404'd into the SPA
// fallback. Wording varies per engine; match all of them.
function isStaleChunkError(error) {
  const msg = String(error?.message || error || '');
  return (
    /not a valid JavaScript MIME type/i.test(msg) ||
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /ChunkLoadError/i.test(msg) ||
    /Importing a module script failed/i.test(msg)
  );
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, stack: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    // THE STACK GOES IN STATE, NOT JUST THE CONSOLE (2026-08-14). It was
    // already logged here — and that was not enough, because a user reporting
    // a crash sends a SCREENSHOT of this card, not their DevTools console.
    // A guest hit a "found: object with keys {}" on Profile and the report
    // arrived naming no component, which is the one fact that would have
    // fixed it in a minute. React builds this stack from the fiber tree, so
    // it names components even in a minified production build.
    this.setState({ stack: info?.componentStack || null });
    // Cloud Logging has no reach here, so the console is the record. Keep the
    // component stack: it names which card threw, which is the whole question.
    console.error('[ErrorBoundary] render failed:', error, info?.componentStack);

    // STALE-CHUNK self-heal (Tom hit it live 2026-07-30, Today → Stats:
    // "'text/html' is not a valid JavaScript MIME type"). The screens are
    // React.lazy chunks with hashed filenames; a tab opened before a deploy
    // asks for a hash that no longer exists, hosting answers the SPA
    // fallback (index.html), and the dynamic import chokes on HTML-as-JS.
    // Nothing is wrong with the code — the tab is just old. The fix is a
    // reload, which fetches the new index.html and the new hashes; guarded
    // once per session so a genuinely broken deploy degrades to the normal
    // error card instead of a reload loop.
    if (isStaleChunkError(error)) {
      try {
        if (!sessionStorage.getItem('ps_chunk_reloaded')) {
          sessionStorage.setItem('ps_chunk_reloaded', '1');
          window.location.reload();
        }
      } catch {
        /* private mode — fall through to the visible error card */
      }
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <main className="ps-page ps-center">
        <p style={{ fontWeight: 800, margin: '0 0 6px' }}>Something broke on this screen.</p>
        <p className="ps-muted" style={{ margin: '0 0 14px', lineHeight: 1.5 }}>
          The rest of the app still works — use the tabs below.
        </p>
        {/* The message, verbatim. Shown rather than swallowed: this is a bug
            report the user can read to us, and it costs nothing to be honest
            about what happened. */}
        <p
          className="ps-muted"
          style={{
            fontSize: 12,
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            margin: '0 0 14px',
            wordBreak: 'break-word',
            opacity: 0.75,
          }}
        >
          {String(this.state.error?.message || this.state.error)}
        </p>
        {/* The component chain, trimmed to the frames that matter. This is
            what turns "something broke" into a named file — and the reason it
            is ON SCREEN rather than console-only is that bug reports arrive as
            screenshots. Small and dimmed so it reads as diagnostics rather
            than as something the user is expected to act on. */}
        {this.state.stack && (
          <pre
            className="ps-muted"
            style={{
              fontSize: 10.5,
              lineHeight: 1.45,
              textAlign: 'left',
              maxWidth: 460,
              margin: '0 auto 14px',
              padding: '8px 10px',
              borderRadius: 8,
              background: 'rgba(255,255,255,0.04)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              opacity: 0.6,
              maxHeight: 132,
              overflow: 'auto',
            }}
          >
            {this.state.stack.trim().split('\n').slice(0, 6).join('\n')}
          </pre>
        )}
        <button
          type="button"
          onClick={() => this.setState({ error: null, stack: null })}
          style={{
            background: 'none',
            border: '1px solid var(--gold)',
            color: 'var(--gold)',
            borderRadius: 999,
            padding: '8px 18px',
            fontSize: 13.5,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          Try again
        </button>
      </main>
    );
  }
}
